/**
 * Public demo-sandbox configuration.
 *
 * These credentials are INTENTIONALLY public — they are printed on the
 * /demo page so a prospective customer can sign in without being given an
 * account. They guard nothing: the accounts are scoped to the
 * `demo-innovacare` org by their custom claims, carry no super_admin role,
 * and therefore cannot read a single real patient (firestore.rules'
 * sameOrg()/canAccessOrg() is what enforces that — the same mechanism that
 * separates every real customer org from every other one).
 *
 * Keep in sync with functions/scripts/seed-demo-sandbox.js, which creates
 * these accounts and the fake data behind them.
 */
export const DEMO_ORG_ID = 'demo-innovacare';

export const DEMO_PASSWORD = 'InnovaCareDemo!2026';

export interface DemoPersona {
  key: 'provider' | 'nurse' | 'admin';
  label: string;
  email: string;
  /** Where to land after sign-in. */
  landingRoute: string;
  icon: string;
  blurb: string;
  /** Concrete things worth clicking, shown on the card so a prospect
   *  isn't left staring at a dashboard wondering what to do. */
  highlights: string[];
}

export const DEMO_PERSONAS: DemoPersona[] = [
  {
    key: 'provider',
    label: 'Provider / NP',
    email: 'provider@demo.innovacare-clinical.app',
    landingRoute: '/provider',
    icon: 'stethoscope',
    blurb: 'Round on wound patients, review and co-sign nurse documentation, and see the financial picture behind each case.',
    highlights: [
      'Provider dashboard and rounds',
      'Review + co-sign wound progress notes',
      'Per-patient Financial & Care Route tab',
    ],
  },
  {
    key: 'nurse',
    label: 'Nurse / RN',
    email: 'nurse@demo.innovacare-clinical.app',
    landingRoute: '/nurse',
    icon: 'vaccines',
    blurb: 'Work a real bedside workflow: structured wound assessments, care plans, tasks, and scheduled visits.',
    highlights: [
      'Structured wound assessments',
      'Care plans, orders and education records',
      'Patient chart with alerts and tasks',
    ],
  },
  {
    key: 'admin',
    label: 'Administrator',
    email: 'admin@demo.innovacare-clinical.app',
    landingRoute: '/profitability',
    icon: 'insights',
    blurb: 'See the business layer: profitability by payer and facility, care-route recommendations, and partner contracts.',
    highlights: [
      'Org-wide profitability portfolio',
      'Cost assumptions and payer rate profiles',
      'HHA partner contracts',
    ],
  },
];
